import { useEffect, useRef, useState } from 'react';
import { ScanLine } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Local camera barcode scanning via the native BarcodeDetector API — no paid
// API, no extra dependency (client 21 July). Supported on Android Chrome and
// Safari 17+; falls back to a message where unavailable so the field is still
// entered by hand.

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (opts?: {
  formats?: string[];
}) => BarcodeDetectorLike;

const FORMATS = [
  'code_128',
  'code_39',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'itf',
  'codabar',
  'qr_code',
  'data_matrix',
];

function detectorCtor(): BarcodeDetectorCtor | null {
  return (
    (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
      .BarcodeDetector ?? null
  );
}

/** Icon button that opens a camera scanner and calls onScan with the code. */
export function ScanButton({
  onScan,
  className,
}: {
  onScan: (value: string) => void;
  className?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={className}
        title={t('scan.scan')}
        aria-label={t('scan.scan')}
        onClick={() => setOpen(true)}
      >
        <ScanLine className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('scan.title')}</DialogTitle>
          </DialogHeader>
          {open && (
            <ScannerView
              onScan={value => {
                onScan(value);
                setOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ScannerView({ onScan }: { onScan: (value: string) => void }) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const Ctor = detectorCtor();
    if (!Ctor) {
      setError('unsupported');
      return;
    }
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const detector = new Ctor({ formats: FORMATS });

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const tick = async () => {
          if (stopped || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            const value = codes[0]?.rawValue;
            if (value) {
              onScanRef.current(value);
              return;
            }
          } catch {
            // ignore a failed frame; keep scanning
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch {
        setError('permission');
      }
    };
    start();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach(track => track.stop());
    };
    // Refs keep onScan current without restarting the camera on re-render.
  }, []);

  if (error) {
    return (
      <p className="text-sm text-muted-foreground">
        {error === 'unsupported' ? t('scan.unsupported') : t('scan.permission')}
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-md bg-black">
      <video ref={videoRef} className="w-full" muted playsInline />
    </div>
  );
}
